import { useState, useEffect } from "react";
import { Save } from "lucide-react";
import { Button } from "antd";
import MediaUpload from "Components/MediaUpload/MediaUpload";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import {
  getServiceAccount,
  patchServiceAccount,
} from "services/serviceAccount.service";
import PageLoader from "Components/PageLoader/PageLoader";

const inputClass =
  "flex h-10 w-full rounded-xl border border-border/50 bg-background px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/30 transition-all";
const readOnlyClass =
  "flex h-10 w-full rounded-xl border border-border/50 bg-muted/50 px-4 py-2 text-sm cursor-not-allowed";

/**
 * Fetches `GET /v1/community-portal/service-account` on mount (when this tab is shown).
 * Editable fields are account-level only; `vendor` is display-only.
 */
function Profile() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [logoDirty, setLogoDirty] = useState(false);
  const [coverDirty, setCoverDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data, errors } = await getServiceAccount();
      if (!cancelled) {
        if (errors?.length > 0) {
          renderErrorNotifications(errors);
        } else {
          setAccount(data);
          setLogoUrl(data?.logo_url || "");
          setCoverUrl(data?.cover_url || "");
        }
        setLogoDirty(false);
        setCoverDirty(false);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!account) return;

    const formData = new FormData(e.target);
    const name = String(formData.get("name") || "").trim();
    const display_name = String(formData.get("display_name") || "").trim();
    if (name.length < 2 || display_name.length < 2) {
      renderErrorNotifications([
        { message: "Name and display name must be at least 2 characters." },
      ]);
      return;
    }

    const description = String(formData.get("description") || "").trim();
    const email = String(formData.get("email") || "").trim();

    try {
      setSaving(true);

      const patchBody = { name, display_name, description };
      if (email) patchBody.email = email;
      if (logoDirty) patchBody.logo_url = logoUrl;
      if (coverDirty) patchBody.cover_url = coverUrl;

      const { errors: patchErrors } = await patchServiceAccount(patchBody);
      if (patchErrors?.length > 0) {
        renderErrorNotifications(patchErrors);
        return;
      }

      const { data: refreshed } = await getServiceAccount();
      setAccount(refreshed);
      setLogoUrl(refreshed?.logo_url || "");
      setCoverUrl(refreshed?.cover_url || "");
      setLogoDirty(false);
      setCoverDirty(false);
      setFormKey((k) => k + 1);
      renderSuccessNotifications({
        title: "Profile updated",
        message: "Your profile changes have been saved.",
      });
    } catch (err) {
      renderErrorNotifications([
        { message: err?.message || "Failed to update profile." },
      ]);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <PageLoader showCards={false} showHero={false} showTabs={false} />;

  if (!account) {
    return (
      <p className="text-sm text-muted-foreground">
        No account data could be loaded.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-border/50 bg-card p-6 md:p-8 shadow-card space-y-6">
        <h3 className="font-bold">Account profile</h3>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <MediaUpload
              value={logoUrl}
              onChange={(url) => {
                setLogoUrl(url || "");
                setLogoDirty(true);
              }}
              listType="picture-circle"
              maxSizeMB={5}
              optimize
              disabled={saving}
            />
            <div className="space-y-1.5 min-w-0">
              <p className="font-bold">Logo</p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Square avatar. JPG, PNG, GIF or WebP. Max 5MB.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <MediaUpload
              value={coverUrl}
              onChange={(url) => {
                setCoverUrl(url || "");
                setCoverDirty(true);
              }}
              listType="picture-card"
              maxSizeMB={5}
              optimize
              disabled={saving}
            />
            <div className="space-y-1.5 min-w-0">
              <p className="font-bold">Cover image</p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Banner-style image. Max 5MB
              </p>
            </div>
          </div>
        </div>

        <form key={formKey} onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <label className="text-sm font-medium">Account ID</label>
              <input
                type="text"
                readOnly
                defaultValue={account.account_id || ""}
                className={readOnlyClass}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <input
                type="text"
                name="name"
                required
                minLength={2}
                maxLength={100}
                defaultValue={account.name || ""}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Display name</label>
              <input
                type="text"
                name="display_name"
                required
                minLength={2}
                maxLength={100}
                defaultValue={account.display_name || ""}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                defaultValue={account.email || ""}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone number</label>
              <input
                type="tel"
                readOnly
                defaultValue={account.phone_number || ""}
                className={readOnlyClass}
                title="Not editable via service-account PATCH."
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                name="description"
                defaultValue={account.description || ""}
                rows={4}
                maxLength={500}
                className={`${inputClass} min-h-[100px] py-3`}
                placeholder="Short description of your studio or organization"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Account types</label>
              <input
                type="text"
                readOnly
                defaultValue={account.account_types?.join(", ") || ""}
                className={readOnlyClass}
                title="Not editable in this form."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Verification status</label>
              <input
                type="text"
                readOnly
                defaultValue={account.verification_status || ""}
                className={readOnlyClass}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Active</label>
              <input
                type="text"
                readOnly
                defaultValue={account.is_active ? "Yes" : "No"}
                className={readOnlyClass}
              />
            </div>
            {account.vendor?.business_documents?.map((doc, i) => (
              <div key={i} className="space-y-2">
                <label className="text-sm font-medium">
                  {doc.document_type}
                </label>
                <input
                  type="text"
                  readOnly
                  defaultValue={doc.document_value}
                  className={readOnlyClass}
                  title="To update this document, please contact ShoUp admin."
                />
              </div>
            ))}
            {account.reason_for_rejection && (
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium">
                  Reason for rejection
                </label>
                <input
                  type="text"
                  readOnly
                  defaultValue={account.reason_for_rejection || ""}
                  className={readOnlyClass}
                />
              </div>
            )}
          </div>
          <div className="pt-4 flex justify-end">
            <Button type="primary" htmlType="submit" loading={saving}>
              <Save className="mr-2 h-4 w-4" /> Save profile
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Profile;
